import React, { useState } from 'react'
import { StringParam, useQueryParams } from 'use-query-params'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useAccountListSearch } from '../../utils/hooks/useAccountListSearch'
import { AccountPermissionsContainer } from '../AccountPermissionsContainer'
import { RemoveAccountModal } from '../remove-account'
import { RedactAccountModal } from '../redact-account'
import { ReactivateAccountModal } from '../reactivate-account'
import { SetTwoFaMandatoryModal } from '../set-twofa-mandatory/SetTwoFaMandatoryModal'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions/usePermissions'
import { useQueryList } from '@percent/utility'
import { SearchInput, Button, Flex } from '@percent/lemonade'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import {
  AccountType,
  AccountListTable,
  AccountPermissions,
  Account,
  InviteUserModal,
  RemoveAdminModal,
  Reset2FAModal
} from '@percent/domain/iam'
import {
  useRemoveAdminModal,
  useInviteUserModal,
  useReset2FAModal,
  useRedactAccountModal
} from '@percent/admin-dashboard/common/hooks'
import type { SectionItem, SectionProps } from '@percent/lemonade'
import { isDefined } from '@percent/admin-dashboard/common/utility'
import { useDeleteAccountModal } from '@percent/admin-dashboard/common/hooks/useDeleteAccountModal'
import { useReactivateAccountModal } from '@percent/admin-dashboard/common/hooks/useReactivateAccountModal'

const getEmployeeTypeOptions = (t: (key: string) => string) => [
  { value: 'goodstack', label: t('modal.accounts.inviteUsers.employeeType.options.goodstack') },
  { value: 'outsourced', label: t('modal.accounts.inviteUsers.employeeType.options.outsourced') },
  { value: 'trusted_partners', label: t('modal.accounts.inviteUsers.employeeType.options.trusted_partners') }
]

export function Members() {
  const { accountService, adminService } = useServices()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { userHasPermissions, canViewRolesOnAnyDomain } = usePermissions()
  const [queryParams, setQueryParams] = useQueryParams({ search: StringParam })

  const [selectedAccount, setSelectedAccount] = useState<Pick<AccountPermissions, 'accountId' | 'accountName'> | null>(
    null
  )
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedMenuAccount, setSelectedMenuAccount] = useState<any>(null)

  const [
    { data, isLoading: isLoadingAccounts, totalResults, errorMessage, status },
    { nextPage, previousPage, refresh }
  ] = useQueryList(['/accounts'], accountService.getAccounts, {
    type: AccountType.internal,
    ...(queryParams.search && { search: queryParams.search })
  })

  const {
    selectedAccount: reset2FAAccount,
    isModalOpen: isReset2FAModalOpen,
    openModal: openReset2FAModal,
    closeModal: closeReset2FAModal,
    reset2FA
  } = useReset2FAModal({ onSuccess: refresh })

  const {
    isModalOpen: isInviteModalOpen,
    modalState: inviteModalState,
    errorMessage: inviteErrorMessage,
    openModal: handleInviteUser,
    closeModal: handleInviteUserCancel,
    confirmInviteUser: handleInviteUserConfirm
  } = useInviteUserModal({ onSuccess: refresh })
  const {
    selectedAccount: selectedAccountForAdminUpdate,
    isModalOpen: isRemoveAdminModalOpen,
    modalState: removeAdminModalState,
    errorMessage: removeAdminErrorMessage,
    openModal: handleRemoveAdminClick,
    closeModal: handleRemoveAdminCancel,
    confirmRemoveAdmin: handleRemoveAdminConfirm
  } = useRemoveAdminModal()

  const [twoFaMandatoryModalOpen, setTwoFaMandatoryModalOpen] = useState(false)

  const {
    selectedAccount: redactAccount,
    isModalOpen: isRedactModalOpen,
    openModal: openRedactModal,
    closeModal: closeRedactModal
  } = useRedactAccountModal()

  const {
    selectedAccount: deleteAccount,
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal
  } = useDeleteAccountModal()

  const {
    selectedAccount: reactivateAccount,
    isModalOpen: isReactivateModalOpen,
    openModal: openReactivateModal,
    closeModal: closeReactivateModal
  } = useReactivateAccountModal()

  const uniquePartnerIds = data
    ? (Array.from(new Set(data.map(account => account.partnerId).filter(Boolean))) as string[])
    : []

  const [{ data: partnersData }] = useQueryList(
    ['/partners', uniquePartnerIds],
    adminService.queries.getPartnerList,
    {
      partnerIds: uniquePartnerIds,
      pageSize: 25
    },
    { enabled: uniquePartnerIds.length > 0 }
  )

  const partnerNames =
    partnersData?.reduce((acc, partner) => {
      acc[partner.id] = partner.name

      return acc
    }, {} as Record<string, string>) || {}

  const { searchValue, handleKeyChange, handleKeyPress, handleClearValue, searchInputStatusMessage } =
    useAccountListSearch({
      queryParams,
      setQueryParams
    })

  const handleAccountRowClick = (accountId: string, accountName: string) => {
    setSelectedAccount({
      accountId,
      accountName
    })
    setIsDrawerOpen(true)
  }

  const handleSet2FAMandatoryClick = async (account: Pick<Account, 'id' | 'fullName' | 'email' | 'twoFaMandatory'>) => {
    setSelectedMenuAccount(account)
    setTwoFaMandatoryModalOpen(true)
  }

  const handleSet2FAMandatoryClose = async () => {
    setSelectedMenuAccount(null)
    setTwoFaMandatoryModalOpen(false)
  }

  const menuItem = (
    key: string,
    label: string,
    onClick: () => void,
    dataTestId: string,
    visible = true
  ): SectionItem | undefined => (visible ? { key, label, onClick, dataTestId } : undefined)

  const getMenuItems = (
    account: Pick<
      Account,
      'id' | 'fullName' | 'email' | 'twoFaSecretVerifiedAt' | 'admin' | 'status' | 'twoFaMandatory' | 'redactedAt'
    >
  ): SectionProps[] => [
    {
      items: [
        menuItem(
          'view-profile',
          t('memberProfile.viewProfile'),
          () => navigate(`/accounts/members/${account.id}`),
          `account-view-profile-${account.id}`,
          userHasPermissions('accounts:can_view')
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.permissions'),
      items: [
        menuItem(
          'edit-permissions',
          t('button.editPermissions'),
          () => handleAccountRowClick(account.id, account.fullName ?? account.email),
          `account-edit-permissions-${account.id}`,
          canViewRolesOnAnyDomain
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.authentication'),
      items: [
        menuItem(
          'set-2fa-mandatory',
          t(`account.modify.set2FAMandatory.${account.twoFaMandatory ? 'disable' : 'enable'}.button`),
          () => handleSet2FAMandatoryClick(account),
          `account-set-2fa-mandatory-${account.id}`,
          userHasPermissions('authentication:can_manage_2fa')
        ),
        menuItem(
          'reset-2fa',
          t('button.reset2FA'),
          () => openReset2FAModal({ id: account.id, email: account.email, fullName: account.fullName ?? null }),
          `account-reset-2fa-${account.id}`,
          userHasPermissions('accounts:can_reset_2fa') && !!account.twoFaSecretVerifiedAt
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.membership'),
      items: [
        menuItem(
          'remove-admin',
          t('button.removeAdmin'),
          () => handleRemoveAdminClick(account),
          `account-remove-admin-${account.id}`,
          userHasPermissions('accounts:can_assign_admin') && !!account.admin
        ),
        menuItem(
          'remove-account',
          t('account.modify.remove.button'),
          async () => openDeleteModal(account),
          `account-remove-${account.id}`,
          userHasPermissions('accounts:can_delete') && account.status !== 'removed'
        ),
        menuItem(
          'reactivate-account',
          t('account.modify.reactivate.button'),
          () => openReactivateModal(account),
          `account-reactivate-${account.id}`,
          userHasPermissions('accounts:can_reactivate') && account.status === 'removed' && !account.redactedAt
        ),
        menuItem(
          'redact-account',
          t('account.modify.redact.button'),
          () => openRedactModal({ id: account.id, email: account.email, fullName: account.fullName ?? null }),
          `account-redact-${account.id}`,
          userHasPermissions('accounts:can_redact')
        )
      ].filter(isDefined)
    }
  ]

  if (errorMessage && status === 'error') {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <AccountListTable
        accounts={data}
        totalResults={totalResults}
        nextPage={nextPage}
        previousPage={previousPage}
        isLoading={isLoadingAccounts}
        getMenuItems={getMenuItems}
        extraColumns={[
          {
            key: 'partner',
            header: t('table.accounts.partnerName'),
            render: account => partnerNames[account.partnerId ?? ''] ?? t('table.accounts.notAvailable')
          }
        ]}
        actionsAndFilters={
          <Flex direction="row" gap="md" align="center" w="100%">
            <SearchInput
              data-testid="members-search-input"
              placeholder={t('table.accountsSearchPlaceholder')}
              value={searchValue}
              onChange={handleKeyChange}
              onKeyDown={handleKeyPress}
              handleClearValue={handleClearValue}
              statusMessage={searchInputStatusMessage}
              status={searchInputStatusMessage ? 'danger' : 'default'}
              width="100%"
            />
            {userHasPermissions('accounts:can_invite') && (
              <Button variant="primary" onClick={handleInviteUser}>
                {t('button.addMember')}
              </Button>
            )}
          </Flex>
        }
      />
      {selectedAccount && (
        <AccountPermissionsContainer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false)
            setSelectedAccount(null)
          }}
          account={selectedAccount}
        />
      )}
      {reset2FAAccount && (
        <Reset2FAModal
          account={reset2FAAccount}
          isOpen={isReset2FAModalOpen}
          onClose={closeReset2FAModal}
          onReset={reset2FA}
        />
      )}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={handleInviteUserCancel}
        onConfirm={handleInviteUserConfirm}
        state={inviteModalState}
        errorMessage={inviteErrorMessage}
        employeeTypeOptions={getEmployeeTypeOptions(t)}
      />
      {selectedAccountForAdminUpdate && (
        <RemoveAdminModal
          isOpen={isRemoveAdminModalOpen}
          onClose={handleRemoveAdminCancel}
          onConfirm={handleRemoveAdminConfirm}
          accountName={selectedAccountForAdminUpdate.fullName || selectedAccountForAdminUpdate.email}
          accountEmail={selectedAccountForAdminUpdate.email}
          state={removeAdminModalState}
          errorMessage={removeAdminErrorMessage}
        />
      )}
      {deleteAccount && (
        <RemoveAccountModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onSuccess={refresh}
          account={deleteAccount}
        />
      )}
      {redactAccount && (
        <RedactAccountModal
          isOpen={isRedactModalOpen}
          onClose={closeRedactModal}
          onSuccess={refresh}
          account={redactAccount}
        />
      )}
      {reactivateAccount && (
        <ReactivateAccountModal
          isOpen={isReactivateModalOpen}
          onClose={closeReactivateModal}
          onSuccess={refresh}
          account={reactivateAccount}
        />
      )}
      {selectedMenuAccount && (
        <SetTwoFaMandatoryModal
          isOpen={twoFaMandatoryModalOpen}
          onClose={handleSet2FAMandatoryClose}
          onSuccess={refresh}
          account={selectedMenuAccount}
        />
      )}
    </>
  )
}
